import { AxiosResponse } from 'axios';
import { IPost, IPostRequest } from '../types/Post';
import { IAggregation } from '../types/Aggregation';
import { IPostRelationships } from '../types/PostRelationships';
import { sdkConfig } from '../config';
import { ITints, ITintsWithSocialsAndPersonalization } from '../types/Tint';
import { IPersonalization } from '../types/Personalization';

export interface ITintsWithInclusions extends ITints, ITintsWithSocialsAndPersonalization {}

export interface PublicPostBody {
  type: string;
  attributes: {
    author: {
      name: string;
      email?: string;
      username: string;
      image_url: string;
    };
    text: string;
    custom_field: string;
    media_url: string[];
    tags?: string;
  };
}

export interface ReviewBody {
  type: string;
  attributes: {
    type?: string;
    author: {
      name?: string;
      email?: string;
    };
    title: string;
    text: string;
    rating: number;
    media_url?: string[];
    tags?: string;
  };
}

export interface IPostsHttpResponseLinks {
  first: string;
  self: string;
  next?: string;
  previous?: string;
}

export interface IGetPostRequestReturnValue {
  data: IPostRequest;
}

export interface IAggregationsHttpResponse {
  data: IAggregation[];
}

export interface IPostsHttpResponse {
  data: IPost[];
  links: IPostsHttpResponseLinks;
  included?: ReadonlyArray<IPostRelationships>;
}

export interface IQueryParam {
  size?: number;
  q?: string;
  social_feed_id?: number[];
  id?: string[];
  language?: string[];
  published_at?: number;
  sentiment?: 'neutral' | 'positive' | 'negative';
  type?: string;
  tag?: string;
  saved_filter_id?: number;
  product_sku?: string;
  rating?: { [key: string]: string } | string;
  sort?: string;
  // source?: string; currently not working
  // status?: string[]; currently not working
  // image_object?: string[]; currently not working
}

export interface IGetPostsReturnValue {
  posts: ReadonlyArray<IPost>;
  postsRelationships?: ReadonlyArray<IPostRelationships>;
  links: IPostsHttpResponseLinks;
}

export interface IGetAggregationsReturnValue {
  aggregations: ReadonlyArray<IAggregation>;
}

export interface ISinglePost {
  post: IPost;
  postsRelationships?: ReadonlyArray<IPostRelationships>;
  links: IPostsHttpResponseLinks;
}

export type TintInclude = 'personalizations' | 'social_feeds' | 'default_personalization';

const axios = require('axios');

export default class ApiService {
  private readonly tintSlug: string;

  constructor(tintSlug: string) {
    this.tintSlug = tintSlug;
  }

  public getSinglePost(id: string): Promise<ISinglePost> {
    return this.queryPosts({ id: [id] }).then(response => {
      const {
        data: { data, included, links },
      } = response;

      return {
        post: data[0],
        postsRelationships: included,
        links,
      };
    });
  }

  public getPosts(config: IQueryParam = {}): Promise<IGetPostsReturnValue> {
    return this.queryPosts(config).then(response => {
      const {
        data: {
          data,
          links: { next, previous, first, self },
          included,
        },
      } = response;

      return {
        posts: data,
        postsRelationships: included,
        links: {
          next,
          previous,
          first,
          self,
        },
      };
    });
  }

  public getAggregations(): Promise<IGetAggregationsReturnValue> {
    return this.getAggregationsData().then(response => {
      const {
        data: { data },
      } = response;

      return {
        aggregations: data,
      };
    });
  }

  public getPage(url: string): Promise<IGetPostsReturnValue> {
    return this.get<IPostsHttpResponse>(url).then(response => {
      const {
        data: {
          data,
          links: { next: nextPage, previous: previousPage, first: firstPage, self },
          included,
        },
      } = response;

      return {
        posts: data,
        postsRelationships: included,
        links: { next: nextPage, previous: previousPage, first: firstPage, self },
      };
    });
  }

  public postReview = (body: ReviewBody) => {
    return this.post(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}/posts`, body);
  };

  public postPublicPost = (body: PublicPostBody) => {
    return this.post(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}/posts`, body);
  };

  public getPublicPostRequest = (requestId: string): Promise<AxiosResponse<IGetPostRequestReturnValue>> => {
    return this.get(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}/posts/requests/${requestId}`);
  };

  public getTintBasicData = (includes: readonly TintInclude[]): Promise<AxiosResponse<ITintsWithInclusions>> => {
    return this.get(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}?include=${includes.join(',')}`);
  };

  public getPersonalization = (personalizationId: number): any => {
    return this.get(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/personalizations/${personalizationId}`);
  };

  private convertDateFrom = (fromValue: number): string => {
    if (fromValue < 0) {
      return `filter[published_at][$gte]=${new Date(Date.now() + fromValue * 1000).toISOString()}&`;
    } else {
      return `filter[published_at][$gte]=${new Date(fromValue * 1000).toISOString()}&`;
    }
  };

  private setQueryParam = (key: string, value: any) => {
    switch (key) {
      case 'sort':
        return `${key}=${value}&`;
      case 'size':
        return `page[size]=${value}&`;
      case 'published_at':
        return this.convertDateFrom(value);
      case 'rating':
        return value.hasOwnProperty('range') ? `filter[${key}][$range]=${value.range}&` : `filter[${key}]=${value}&`;
      default:
        return `filter[${key}]=${value}&`;
    }
  };

  private mapConfigToQueryParams = (config: IQueryParam): string =>
    Object.keys(config).reduce((acc, curr) => {
      if (config[curr] !== undefined) {
        const value = Array.isArray(config[curr]) ? config[curr].join(',') : config[curr];
        const nextParam = this.setQueryParam(curr, value);

        return `${acc}${nextParam}`;
      }
      return `${acc}`;
    }, '');

  private queryPosts = (config: IQueryParam): Promise<AxiosResponse<IPostsHttpResponse>> => {
    return this.get(
      `${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}/posts?${this.mapConfigToQueryParams(
        config
      )}include=product_tags.product,cta_associations.cta&`
    );
  };

  private getAggregationsData = (): Promise<AxiosResponse<IAggregationsHttpResponse>> => {
    return this.get(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}/posts/aggregations`);
  };

  private get = <T, R = AxiosResponse<T>>(url: string, requestId?: string): Promise<R> => {
    return axios.get(url, { requestId });
  };

  private post = <T, R = AxiosResponse<T>>(url: string, payload: T): Promise<R> => {
    return axios.post(url, { data: payload }, { headers: { 'Content-Type': 'application/vnd.api+json' } });
  };
}
